import { useEffect, useCallback, useState } from 'react';
import { ComponentConfig, FieldLabel } from '@measured/puck';
import { setCheckoutErrorMessage } from '../../redux/slices/commonSlice';
import { useFormContext } from 'react-hook-form';
import { setApiLoading } from '../../redux/slices/checkoutSlice';
import { setAvailableShippingMethods } from '../../redux/slices/checkoutSlice';
import useDebounce from '../../hooks/useDebounce';
import checkoutService from '../../api/checkoutService';
import {
  ContactInformationFormType,
  IAddressOptions,
} from '../../../page-builder/interface/formInterface';
import { V2ShippingMethod } from '../../../page-builder/interface/checkoutInterface';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import {
  defaultBorderProps,
  emailRegExp,
  getIsViewIsEdit,
  toolTipInfo,
} from '../../../page-builder/utils/constants';
import { TextFieldControl } from '../form-fields/TextField';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import CustomTooltip from '../../../components/common/tooltip';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import { EventPublisher } from '../../utils/publishEvent';
import { ExtractPhxRequestId } from '../../utils/extractPhoenixId';
import './../text-editor/style.scss';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
  restrictEmojis,
  storePhoneCode,
} from '../../utils/helper';
import { AddressFormField } from '../../puck/reusable-props/addressFormOptions';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import {
  fieldHeightProps,
  inputFieldHeightProps,
} from '../../puck/reusable-props/inputfield-height';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { marginProps } from '../../puck/reusable-props/NewMargin';
import WithAccordion from '../customComponents/CustomAccordion';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import Select from '../../../components/common/select';
import Toggle from '../../../components/common/toggleSwitchCustom';
import { outlineProps } from '../../puck/reusable-props/Outline';

export type ContactInfoProps = {
  hideCheckBox: boolean;
  newsLetter: string;
  option: {
    Email: IAddressOptions;
  };
  border: IBorderValueProps;
  checkboxColor: string;
  customCheckbox?: {
    checkedBorderColor?: string;
    uncheckedBorderColor?: string;
    checkboxshape?: string;
    checkboxSize?: string;
    enrollmentStyle?: string;
    checkMarkColor?: string;
    checkedToggleColor?: string;
    uncheckedToggleColor?: string;
    toggleSize?: string;
    checkMarkSize?: string;
  };
} & MarginProps &
  fieldHeightProps &
  PaddingProps;

export const ContactInfo = ({
  useAppDispatch,
  useAppSelector,
}: ReduxSelectorProps &
  ReduxDispatchProps): ComponentConfig<ContactInfoProps> => {
  return {
    label: (
      <CustomTooltip title="ContactInfo">
        <span>Contact Info</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
      option: {
        type: 'custom',
        label: 'Options',
        render: ({ value, onChange }) => {
          return (
            <div>
              {Object?.keys(value || {})?.map(key => {
                return (
                  <AddressFormField
                    formName="Contact Info"
                    key={key}
                    name={key}
                    value={value?.[key as keyof { Email: IAddressOptions }]}
                    onChange={newValue =>
                      onChange({ ...value, [key]: newValue })
                    }
                    index={50}
                  />
                );
              })}
            </div>
          );
        },
      },
      hideCheckBox: {
        type: 'radio',
        label: 'Hide News Letter',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
      checkboxColor: {
        type: 'custom',
        label: 'Selected Checkbox color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      customCheckbox: {
        type: 'custom',
        label: 'Custom Checkbox',
        render: ({ value, onChange }) => {
          const options = [
            { label: 'Square', value: 'rounded' },
            { label: 'Circle', value: 'rounded-full' },
          ];
          return (
            <WithAccordion title="Custom Checkbox">
              <>
                <div>
                  <ColorPicker
                    value={value?.checkedBorderColor}
                    onChange={color =>
                      onChange({ ...(value || {}), checkedBorderColor: color })
                    }
                    label="Checked Border Color"
                    tooltip="Checked Border Color"
                  />
                </div>
                <div>
                  <ColorPicker
                    value={value?.uncheckedBorderColor}
                    onChange={color =>
                      onChange({
                        ...(value || {}),
                        uncheckedBorderColor: color,
                      })
                    }
                    label="Unchecked Border Color"
                    tooltip="Unchecked Border Color"
                  />
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Shape' as keyof typeof toolTipInfo}>
                          <span>Checkbox Shape</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <div className="w-full p-2">
                      <ToggleButtonGroup
                        value={value?.checkboxshape}
                        exclusive
                        className="w-full"
                        onChange={(_e, selectValue) => {
                          selectValue &&
                            onChange({ ...value, checkboxshape: selectValue });
                        }}>
                        {options.map((option, index: number) => (
                          <ToggleButton
                            key={index}
                            size="small"
                            value={option.value}
                            disableRipple
                            aria-label={option.value}
                            className="flex-1">
                            {option.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </div>
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Size' as keyof typeof toolTipInfo}>
                          <span>Checkbox Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkboxSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkboxSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter checkbox size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="EnrollementStyle">
                          <span>Enrollement Style</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                  />
                  <Select
                    value={value?.enrollmentStyle}
                    onChange={val =>
                      onChange({ ...value, enrollmentStyle: val })
                    }
                    options={[
                      {
                        label: 'Checkbox',
                        value: 'checkbox',
                      },
                      {
                        label: 'Toggle',
                        value: 'toggle',
                      },
                    ]}
                  />
                </div>
                <ColorPicker
                  value={value?.checkMarkColor || '#ffffff'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkMarkColor: color,
                    })
                  }
                  label={'Check Mark Color'}
                  tooltip={'Check Mark Color'}
                />
                <ColorPicker
                  value={value?.checkedToggleColor || '#4f46e5'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkedToggleColor: color,
                    })
                  }
                  label={'Checked Toggle Color'}
                  tooltip={'Checked Toggle Color'}
                />
                <ColorPicker
                  value={value?.uncheckedToggleColor || '#d1d5db'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      uncheckedToggleColor: color,
                    })
                  }
                  label={'Unchecked Toggle Color'}
                  tooltip={'Unchecked Toggle Color'}
                />
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Toggle Size' as keyof typeof toolTipInfo}>
                          <span>Toggle Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.toggleSize || 20}
                      onChange={e =>
                        onChange({
                          ...value,
                          toggleSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter toggle size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Check Mark Size' as keyof typeof toolTipInfo}>
                          <span>Check Mark Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkMarkSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkMarkSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter check mark size"
                    />
                  </FieldLabel>
                </div>
              </>
            </WithAccordion>
          );
        },
      },
      ...inputFieldHeightProps(true),
      newsLetter: {
        ...QuillTextEditor('News Letter').content,
      },
    },
    defaultProps: {
      newsLetter:
        '<p style="line-height:1.5;"><span style="font-size: 14px;">Email me with news and offers</span></p>',
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      heightField: {
        height: {
          desktop: '42',
          tablet: '42',
          mobile: '42',
        },
        rowGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        colGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        deviceType: 'desktop',
      },
      ...paddingProps,
      ...borderProps,
      option: {
        Email: {
          header: {
            hide: false,
            text: 'Email',
            ...paddingProps,
            ...marginProps,
          },
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          placeholder: 'Email',
          show: true,
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'Email is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
      },
      hideCheckBox: true,
      checkboxColor: '#4f46e5',
      customCheckbox: {
        checkedBorderColor: '#4f46e5',
        uncheckedBorderColor: '#d1d5db',
        checkboxshape: 'rounded',
        checkboxSize: '16',
        enrollmentStyle: 'checkbox',
        checkMarkColor: '#ffffff',
        checkedToggleColor: '#4f46e5',
        uncheckedToggleColor: '#d1d5db',
        toggleSize: '20',
        checkMarkSize: '16',
      },
    },
    render: ({
      margin,
      option,
      newsLetter,
      hideCheckBox,
      heightField,
      editMode,
      padding,
      border,
      checkboxColor,
      customCheckbox,
    }) => {
      const dispatch = useAppDispatch();
      const {
        watch,
        formState: { errors, dirtyFields },
      } = useFormContext<ContactInformationFormType>();
      const isEmailDirty = !!dirtyFields?.CustomerData?.Email;
      const isPhoneDirty = !!dirtyFields?.CustomerData?.PhoneNumber;
      const [optNewsLetter, setOptNewsLetter] = useState(true);
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const storeData = useAppSelector(state => state.store.storeData);
      const { cartData } = useAppSelector(state => state.cart);
      const { pageId } = useAppSelector(state => state.checkout);
      const { Email, PhoneNumber } = watch('CustomerData');
      const PhoneCode = watch('phone_code');
      const state = useAppSelector(state => state);
      const newInterpolation = getQuillEditorText(newsLetter, state);
      const [check, setCheck] = useState(true);

      const isCustomerDetailsValid = !!Email && !errors?.CustomerData?.Email;
      const publish = EventPublisher.getInstance().publish;

      const updateCustomerDetails = useCallback(async () => {
        if (
          isCustomerDetailsValid &&
          cartData?.ID &&
          storeData?.ShopID &&
          storeData?.StoreID
        ) {
          const payload = {
            Email,
            PhoneNumber: PhoneNumber ? `${PhoneCode}${PhoneNumber}` : null,
            ID: cartData.ID,
            ShopID: storeData.ShopID,
            StoreID: Number(storeData.StoreID),
            PageID: String(pageId) || '',
          };
          storePhoneCode(PhoneCode);

          dispatch(setApiLoading(true));
          try {
            const res = await checkoutService.setContactInfo(payload);
            if (res?.data) {
              if (!getIsViewIsEdit()) {
                publish('CustomerContact', {
                  eventName: 'CustomerContact',
                  eventId: ExtractPhxRequestId(res),
                  shopId: storeData.ShopID,
                  cart: res?.data?.Cart,
                });
              }
              if (res.data?.AvailableShippingMethods) {
                setAvailableShippingMethods(
                  res.data.AvailableShippingMethods as V2ShippingMethod[],
                );
              } else if (res.data?.Cart?.AvailableShippingMethods) {
                setAvailableShippingMethods(
                  res.data.Cart.AvailableShippingMethods as V2ShippingMethod[],
                );
              }
            }
          } catch (error: unknown) {
            if (error instanceof Error) {
              dispatch(setCheckoutErrorMessage(error.message));
              return;
            }

            dispatch(setCheckoutErrorMessage('An unknown error occurred'));
          }
        }
        dispatch(setApiLoading(false));
      }, [
        dispatch,
        isCustomerDetailsValid,
        Email,
        PhoneNumber,
        PhoneCode,
        cartData?.ID,
        storeData?.ShopID,
        storeData?.StoreID,
      ]);

      const updateCustomerDetailsDebounce = useDebounce(
        updateCustomerDetails,
        500,
      );

      useEffect(() => {
        if (isEmailDirty || isPhoneDirty) updateCustomerDetailsDebounce();
      }, [
        isCustomerDetailsValid,
        Email,
        errors.CustomerData?.Email,
        updateCustomerDetailsDebounce,
      ]);
      const marginStyles = getMarginStyles(margin, currentDeviceType);

      return (
        <div className="text-editor-content">
          <section
            className={`${border?.borderStyle?.[currentDeviceType]}`}
            aria-labelledby="contact-info-heading"
            style={{
              ...marginStyles,
              ...getPaddingStyles(padding, currentDeviceType),
              ...getBorderProps(border, currentDeviceType),
            }}>
            {option?.Email?.show && (
              <TextFieldControl
                name="CustomerData.Email"
                onChangeHandler={restrictEmojis}
                placeholder={option?.Email?.placeholder}
                placeHolderColor={option?.Email?.color}
                textColor={option?.Email?.textColor}
                header={option?.Email?.header}
                backgroundColor={option?.Email?.textBackgroundColor}
                placeHolderFontSize={option?.Email?.fontSize}
                placeHolderweight={option?.Email?.fontWeight}
                PlaceHolderTextAlign={option?.Email?.textAlign}
                placeHolderFontFamily={option?.Email?.fontFamily}
                placeHolderLetterSpace={option?.Email?.letterSpace}
                fieldHeight={heightField?.height?.[currentDeviceType]}
                currentDeviceType={currentDeviceType}
                radiusBorder={option?.Email?.border}
                outline={option?.Email?.outline}
                rules={{
                  required: option?.Email?.requiredValidation
                    ? option?.Email?.validationText ||
                      'Please enter your email address'
                    : false,
                  pattern: {
                    value: emailRegExp,
                    message: 'Please enter a valid email address',
                  },
                }}
              />
            )}
            {!hideCheckBox && (
              <div className="flex items-center mt-4">
                {customCheckbox?.enrollmentStyle === 'checkbox' ? (
                  <input
                    id="comments"
                    aria-describedby="comments-description"
                    aria-label="comments-description"
                    name="terms_of_service"
                    type="checkbox"
                    className={`h-4 w-4 ${customCheckbox?.checkboxshape} border-gray-300 cursor-pointer`}
                    style={{
                      color: checkboxColor,
                      width: `${customCheckbox?.checkboxSize}px`,
                      height: `${customCheckbox?.checkboxSize}px`,
                      borderColor: optNewsLetter
                        ? customCheckbox?.checkedBorderColor
                        : customCheckbox?.uncheckedBorderColor,
                    }}
                    onChange={e => setOptNewsLetter(e.target.checked)}
                    checked={optNewsLetter}
                  />
                ) : (
                  <Toggle
                    checked={check}
                    aria-describedby="comments-description"
                    aria-label="comments-description"
                    style={{
                      backgroundColor: check
                        ? customCheckbox?.checkedToggleColor
                        : customCheckbox?.uncheckedToggleColor,
                      color: customCheckbox?.checkMarkColor,
                      height: `${customCheckbox?.toggleSize}px`,
                      width: `${customCheckbox?.toggleSize}px`,
                    }}
                    circleWidth={`${customCheckbox?.checkMarkSize}px`}
                    circleHeight={`${customCheckbox?.checkMarkSize}px`}
                    handleToggle={() => {
                      setCheck(!check);
                      setOptNewsLetter(!check);
                    }}
                  />
                )}
                <div
                  style={{ lineHeight: 1.5 }}
                  className="ml-3 text-sm text-gray-900"
                  dangerouslySetInnerHTML={{ __html: newInterpolation }}
                />
              </div>
            )}
          </section>
        </div>
      );
    },
  };
};
